import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useOnboarding } from '../../context/OnboardingContext';
import { ROLE_SUBTITLES, TOUR_TIME_ESTIMATE, TOUR_STEPS } from '../../lib/tourSteps';
import { ShieldCheck } from 'lucide-react';

export default function WelcomeModal() {
  const { user } = useAuth();
  const { showWelcome, startTour, resumeIndex } = useOnboarding();

  if (!showWelcome || !user) return null;

  const role = user.role || 'levantador';
  const steps = TOUR_STEPS[role] || [];
  // resumeIndex sale del contexto (último paso registrado por la API + 1).
  const hasSaved = resumeIndex > 0;

  // Custom title for super_admin
  const getTitle = () => {
    if (role === 'super_admin') {
      return `¡Hola, ${user.name?.split(' ')[0] || 'Super'}! Bienvenido a SOEM. 👋`;
    }
    return `¡Bienvenido a SOEM, ${user.name?.split(' ')[0]}! 👋`;
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg">
        {/* Header - No X button for forced completion */}
        <div className="bg-soem-navy px-6 py-5 rounded-t-sm relative">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-soem-primary rounded-sm flex items-center justify-center">
              <ShieldCheck size={22} className="text-white" />
            </div>
            <span className="text-white font-black text-xl">SOEM</span>
          </div>
          <h2 className="text-white font-bold text-xl leading-tight">
            {getTitle()}
          </h2>
          <p className="text-slate-300 text-sm mt-1">{ROLE_SUBTITLES[role]}</p>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Time estimate */}
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-sm px-4 py-2.5">
            <span className="text-lg">⏱️</span>
            <p className="text-blue-800 text-sm">Este recorrido toma aproximadamente <strong>{TOUR_TIME_ESTIMATE[role]}</strong></p>
          </div>

          {/* 3 features */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { icon: '📋', label: 'Módulos principales' },
              { icon: '⚡', label: 'Funciones clave' },
              { icon: '🚀', label: 'Cómo empezar' },
            ].map(f => (
              <div key={f.label} className="flex flex-col items-center gap-2 bg-slate-50 rounded-sm py-4 px-2 text-center">
                <span className="text-2xl">{f.icon}</span>
                <p className="text-xs text-slate-600 font-medium">{f.label}</p>
              </div>
            ))}
          </div>

          {/* Resume option */}
          {hasSaved && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-sm px-4 py-3 text-sm text-yellow-800">
              Llevas <strong>{resumeIndex}</strong> de {steps.length} pasos. ¿Continuar donde te quedaste?
            </div>
          )}

          {/* Actions - Only start/resume buttons, NO skip button */}
          <div className="flex flex-col gap-2">
            {hasSaved ? (
              <>
                <button onClick={() => startTour(resumeIndex)}
                  className="soem-btn-primary py-3 text-sm font-semibold" data-testid="resume-tour-btn">
                  Continuar en el paso {resumeIndex + 1} de {steps.length}
                </button>
                <button onClick={() => startTour(0)}
                  className="soem-btn-secondary py-2.5 text-sm">
                  Empezar desde el inicio
                </button>
              </>
            ) : (
              <button onClick={() => startTour(0)}
                className="soem-btn-primary py-3 text-sm font-semibold" data-testid="start-tour-btn">
                Iniciar recorrido ({steps.length} pasos)
              </button>
            )}
            {/* REMOVED: "Explorar por mi cuenta" button */}
          </div>
        </div>
      </div>
    </div>
  );
}
